import { DecoratedFormProps, InjectedFormProps, reduxForm } from 'redux-form';
import { FormError } from 'src/components/redux-form/FormError';
import React, { useState } from 'react';
import {
    emailList,
    maxLength,
    minLength,
    minPhoneLength,
    required,
    Validators,
} from 'src/components/redux-form/utils/validators';
import classNames from 'classnames';
import { FieldInput } from 'src/components/redux-form/fields/FieldInput';
import styles from '../assets/password-recovery-screen.module.scss';
import { faSave } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Dispatch } from 'redux';
import { toast } from 'react-toastify';
import { FieldDate } from '../../../../../components/redux-form/fields/FieldDate';
import { asyncPersistIntakeConsentTestingDoeCases } from '../../../../../store/actions/intakeSent-actions';
import { push } from 'connected-react-router';
import Router from '../../../../../navigation/router';
import { routes } from '../../../../../navigation';

export interface ConsentTestingDoeCasesFormData {
    student: string;
    formId: string;
    fullName: string;
    childName: string;
    attorneyName: string;
    onBehalfName: string;
    childDob: string;
    parent: string;
    parentDate: string;
    witnessSignature: string;
    witnessDate: string;
}

interface ConsentTestingDoeCasesFormProps {
    initialValues: any;
    setIsLoader: any;
}

type InjectedProps = InjectedFormProps<ConsentTestingDoeCasesFormData, ConsentTestingDoeCasesFormProps>;

const validators: Validators<ConsentTestingDoeCasesFormData> = {
    childName: [required({ message: "It's required." }), maxLength({ max: 255 })],
    attorneyName: [required({ message: "It's required." }), maxLength({ max: 255 })],
    onBehalfName: [required({ message: "It's required." }), maxLength({ max: 255 })],
    childDob: [required({ message: "It's required." }), maxLength({ max: 255 })],
    parent: [required({ message: "It's required." }), maxLength({ max: 255 })],
    parentDate: [required({ message: "It's required." }), maxLength({ max: 255 })],
    witnessDate: [required({ message: "It's required." }), maxLength({ max: 255 })],
    fullName: [required({ message: "It's required." }), maxLength({ max: 255 })],
    witnessSignature: [required({ message: "It's required." }), maxLength({ max: 255 })],
};

const ConsentTestingDoeCasesForm: React.FunctionComponent<InjectedProps & ConsentTestingDoeCasesFormProps> = ({
    handleSubmit,
    error,
    dirty,
    submitting,
    initialValues,
    setIsLoader,
}: InjectedProps & ConsentTestingDoeCasesFormProps) => {
    return (
        <form onSubmit={handleSubmit} className={classNames(styles.form)}>
            <h2>Informed Consent for Testing</h2>
            <div>[DOE evaluation]</div>
            <FormError error={error} />
            <FieldInput
                name="student"
                type="hidden"
                wrapperConfig={{
                    label: '',
                }}
            />
            <FieldInput
                name="formId"
                type="hidden"
                value={14}
                wrapperConfig={{
                    label: '',
                }}
            />

            <FieldInput
                name="fullName"
                type="text"
                validate={validators.fullName}
                wrapperConfig={{
                    label: 'Parent/Guardian Full Name: *',
                }}
            />
            <FieldInput
                name="childName"
                type="text"
                validate={validators.childName}
                wrapperConfig={{
                    label: 'Child’s Full Name: *',
                }}
            />
            <FieldInput
                name="attorneyName"
                type="text"
                validate={validators.attorneyName}
                wrapperConfig={{
                    label: 'Attorney’s Full Name: *',
                }}
            />

            <div>
                You have been referred to Manhattan Psychology Group for an evaluation for your child. You were referred
                to Manhattan Psychology Group by your attorney, listed above.
                <br />
                <br />
                <b>Nature and Purpose of Assessment:</b> The goal of neuropsychological assessment is to better understand your
                child’s neuropsychological status and functions. This may include testing to assess attention, memory,
                language, problem solving, executive functions, academic levels, intelligence/cognitive ability, or
                other cognitive functions. Assessment of your child may also include assessment of their motor skills,
                adaptive functioning and social-emotional/behavioral functioning. A neuropsychological assessment may
                indicate changes in brain function and suggest possible methods and treatments for rehabilitation or
                interventions in academic/educational settings. A psychoeducational evaluation, while very similar to a
                neuropsychological evaluation, focuses more on cognitive ability and academic achievement, and is a less
                in-depth assessment of neurocognitive abilities. In addition to an intake interview where we will be
                asking you questions about your child’s background, history, and current situation at school, we will be
                using different techniques and standardized tests with your child including but not limited to asking
                questions about knowledge of certain topics, reading, drawing figures and shapes, viewing printed
                material, and manipulating objects. In some cases, evaluation of your child may include observation of
                your child in a school setting, as well as contact with his/her teachers, both only with your consent.
                <br />
                <br />
                <b>Foreseeable Risks, Discomforts, and Benefits:</b> For some individuals, assessments can cause fatigue,
                frustration, and anxiousness. For children who have behavioral and/or emotional difficulties, testing
                may trigger emotional/behavioral distress. We will make every effort to reassure your child and keep
                him/her safe as well. We can take steps to ensure your child’s comfort, such as discussing the
                procedures ahead of time and taking breaks as needed. We ask that you remain in the waiting room during
                your child’s evaluation in case he/she may need to reconnect with a parent during testing.
                <br />
                <br />
                <b>Fees and Time Commitment:</b> Assessments typically take several hours or more of face-to-face testing and
                several additional hours for scoring, interpretation, and report preparation. Evaluations vary
                considerably in how long they take to complete. Depending on the type of evaluation requested, and
                depending on your child’s ability to engage in standardized testing, the amount of time required varies
                widely. Neuropsychological evaluations can take 6-8 hours of time to complete standardized testing with
                your child. This is only an estimate, and the actual time required may be different, depending on your
                child. It may take a few weeks after testing is completed to prepare a summary report. After the report
                is complete, you can request a feedback session if you would like to discuss the findings with the
                evaluator. The overall process can require as many as 15-20 hours from the evaluator, and cover several
                weeks. You and your child will be directly involved in approximately 6-10 hours of time during the
                process.
                <br />
                <br />
                Fees for evaluations [such as this one] referred by attorneys subsequent to Interim Orders of an
                Impartial Hearing Officer requiring the New York City Department of Education to pay for an evaluation,
                will be handled directly between Manhattan Psychology Group and the referring attorney and/or the New
                York City Department of Education. You will not have to provide any payment for your child’s evaluation
                to the Manhattan Psychology Group.
                <br />
                <br />
                <b>Limits of Confidentiality:</b> Information obtained during assessments is confidential and can ordinarily be
                released only with your written permission. There are some special circumstances that can require by law
                that Manhattan Psychology Group share otherwise confidential information. These included but may not be
                limited to: a) a statement of intent to harm self or others, b) statements indicating harm or suspected
                abuse of children or vulnerable/elderly adults; and c) a subpoena or order from a court of law. In any
                of these circumstances the information shared will be limited as much as possible to meet only the legal
                requirements of the state.
                <br />
                <br />
                <b>Permission to Release Information and/or Report to Referring Attorney:</b> Because my child’s evaluation is
                requested by the attorney who referred me to Manhattan Psychology Group, my signature below indicates
                that I give my permission for Manhattan Psychology Group to share information with the referring
                attorney [named below] about my child and my family. I also agree to allow Manhattan Psychology Group to
                provide the summary report to the referring attorney.
                <br />
                I also understand that in some cases, an evaluator or staff member from Manhattan Psychology Group may
                be asked to give testimony at a hearing. In such cases I would hear the evaluator or staff member
                testify about my child and the findings from my child’s evaluation.
                <br />
                <br />
                <br />
                <br />
                <br />
                <br />
                I have read and agree with the nature and purpose of this assessment and with each of the points listed
                above. I have had an opportunity to clarify any questions and discuss any points of concern before
                consenting to my child’s evaluation. My signature below indicates that I am giving my permission to
                Manhattan Psychology Group to conduct an evaluation of my child, and to share the summary report with
                the referring attorney. My signature below indicates that all of my questions have been answered to my
                satisfaction. I am in agreement with the terms above, and agree to allow my child to undergo a
                neuropsychological evaluation.
                <br />
                <br />
            </div>

            <FieldInput
                name="onBehalfName"
                type="text"
                validate={validators.onBehalfName}
                wrapperConfig={{
                    label: 'On Behalf of [child’s name]: *',
                }}
            />
            <FieldDate
                name="childDob"
                type="text"
                date={true}
                validate={validators.childDob}
                wrapperConfig={{
                    label: 'Child’s date of birth: *',
                }}
            />
            <FieldInput
                name="parent"
                type="text"
                validate={validators.parent}
                wrapperConfig={{
                    label: 'Parent/Guardian: *',
                }}
            />
            <FieldDate
                name="parentDate"
                type="text"
                date={true}
                validate={validators.parentDate}
                wrapperConfig={{
                    label: 'Date *',
                }}
            />
            <FieldInput
                name="witnessSignature"
                type="text"
                validate={validators.witnessSignature}
                wrapperConfig={{
                    label: 'Witness Signature: *',
                }}
            />
            <FieldDate
                name="witnessDate"
                type="text"
                date={true}
                validate={validators.witnessDate}
                wrapperConfig={{
                    label: 'Date *',
                }}
            />

            <div className={classNames(styles.actions)}>
                <button
                    type="submit"
                    className={classNames('btn', 'btn-success', 'mr-3')}
                    disabled={!dirty || submitting}
                >
                    <FontAwesomeIcon icon={faSave} /> Confirm and Send
                </button>
            </div>
        </form>
    );
};

export default reduxForm<ConsentTestingDoeCasesFormData, ConsentTestingDoeCasesFormProps>({
    form: 'consent-testing-doe-cases-form',
    enableReinitialize: true,
    onSubmit: async (
        data: ConsentTestingDoeCasesFormData,
        dispatch: Dispatch<any>,
        props: ConsentTestingDoeCasesFormProps,
    ) => {
        props.setIsLoader(true);
        await asyncPersistIntakeConsentTestingDoeCases({ ...data }, dispatch);
    },
    onSubmitSuccess(
        result: any,
        dispatch: Dispatch<any>,
        props: DecoratedFormProps<ConsentTestingDoeCasesFormData, ConsentTestingDoeCasesFormProps, string>,
    ) {
        props.setIsLoader(false);
        //toast.success(`The form was successfully sent.`);
        dispatch(push(Router.generate(routes.INTAKE_PARENT_FORM_SUCCESS, {})));
    },

    onSubmitFail(errors: { _error: string }) {
        // props.setIsLoader(false);
        if (errors) {
            let field: any = null;
            Object.keys(errors).map((e: any, index: number, array: any) => {
                if (!field) {
                    field = e;
                    return;
                }
                return;
            });
            if (field) {
                const section = document.querySelector(`label[for='${field}']`);
                section?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        }
        if (errors._error) {
            toast.error(errors._error);
        }
    },
})(ConsentTestingDoeCasesForm);
